import { useTranslation } from 'react-i18next';

import { Fade, Grid, IconButton, Link, Typography } from '@mui/material';

import {
  trackClickBannerStructuredFbClose,
  trackClickBannerStructuredFbCTA,
} from '../../utils/analytics/analytics';
import {
  setLegacyPreviewBannerClicked,
  setLegacyPreviewBannerDismissed,
} from '../../utils/db';
import { useFirebaseLoginState } from '../../utils/hooks';
import { Item, LoadingStatus } from '../../utils/types';
import { IconArchive } from '../minicomponents/icons';

interface LegacyPreviewBannerProps {
  campaignId: string;
  onOpen: () => void;
  legacyItems: Item[];
}

export function LegacyPreviewBanner({
  campaignId,
  onOpen,
  legacyItems,
}: LegacyPreviewBannerProps) {
  const { t } = useTranslation();
  const { userId } = useFirebaseLoginState();

  // Only show banner if there are completed legacy items
  const completedLegacyItems = legacyItems.filter(
    (item) => item.status === LoadingStatus.COMPLETED
  );

  if (completedLegacyItems.length === 0) {
    return null;
  }

  const handleDismiss = async () => {
    await setLegacyPreviewBannerDismissed(userId, 'facebook', campaignId);
    await trackClickBannerStructuredFbClose();
  };

  const handleCTAClick = async () => {
    await setLegacyPreviewBannerClicked(userId, 'facebook', campaignId);
    await trackClickBannerStructuredFbCTA();
    onOpen();
  };

  return (
    <Fade in timeout={800}>
      <Grid
        container
        spacing={2}
        sx={{
          padding: '16px 24px',
          mb: 3,
          bgcolor: 'rgba(66, 133, 244, 0.08)',
          borderRadius: 1,
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <IconButton
          aria-label="close"
          size="medium"
          onClick={handleDismiss}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            '&:hover': {
              bgcolor: 'rgba(0,0,0,0.05)',
            },
            borderRadius: '50%',
            p: 0.75,
          }}
        >
          {/* <IconClose /> */}
        </IconButton>

        <Grid size="auto">
          <IconArchive sx={{ color: 'text.secondary', fontSize: '3rem', mr: 1 }} />
        </Grid>

        <Grid size="grow">
          <Typography variant="body2">
            <strong>{t('app.legacy.facebook.banner.title')}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('app.legacy.facebook.banner.description')}
          </Typography>
          <Link
            component="button"
            onClick={handleCTAClick}
            sx={{
              bgcolor: 'white',
              color: 'primary.dark',
              px: 2,
              py: 1,
              borderRadius: 1,
              fontWeight: 500,
              textTransform: 'none',
              textDecoration: 'none',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            {t('app.legacy.facebook.banner.cta')}
          </Link>
        </Grid>
      </Grid>
    </Fade>
  );
}
